import { useEffect, type PropsWithChildren } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Toaster } from 'sonner';

import { AuthBootstrap } from '@/app/providers/auth-bootstrap';
import { DesktopCopilotProvider } from '@/app/providers/desktop-copilot-provider';
import { flushOfflineQueue } from '@/lib/offline-sync';
import { queryClient, queryPersister } from '@/lib/query-client';
import { useUiStore } from '@/state/ui-store';

function ThemeController() {
  const theme = useUiStore((state) => state.theme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme === 'light');
  }, [theme]);

  return null;
}

function OfflineSyncBridge() {
  useEffect(() => {
    const sync = () => void flushOfflineQueue();
    window.addEventListener('online', sync);
    void sync();
    return () => window.removeEventListener('online', sync);
  }, []);

  return null;
}

export function AppProviders({ children }: PropsWithChildren) {
  const content = (
    <>
      <ThemeController />
      <AuthBootstrap />
      <OfflineSyncBridge />
      <DesktopCopilotProvider>{children}</DesktopCopilotProvider>
      <Toaster
        theme="dark"
        toastOptions={{
          className: 'border border-white/10 bg-ink-900 text-slate-100',
        }}
      />
    </>
  );

  if (!queryPersister) {
    return <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 1000 * 60 * 60 * 24,
      }}
    >
      {content}
    </PersistQueryClientProvider>
  );
}
